import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CloseShiftSection } from '@/components/pos/CloseShiftSection';
import { useCurrentShift } from '@/hooks/queries';
import { formatDate, formatTime, rupiah, todayISO } from '@/lib/format';
import type { Shift } from '@/types/sale';

/**
 * Tutup shift — only the drawer count and the close, nothing else (Beranda's
 * "Tutup shift" lands here). The full day's summary stays on the Shift page.
 * Reachable with no shift open (`RequireOpenShift`) only so the closed result
 * and its print stay on screen after closing; with nothing just closed it
 * sends the cashier back to `/`.
 */
export function CloseShiftPage() {
  const { data: current, isLoading } = useCurrentShift();
  const [closed, setClosed] = useState<Shift | null>(null);

  return (
    <div className="mx-auto h-full max-w-4xl overflow-y-auto px-6 py-8 pos-scroll">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-900">Tutup Shift</h1>
          <p className="text-sm text-muted-foreground">{formatDate(todayISO())}</p>
        </div>
        <Button asChild variant="ghost" className="gap-2">
          <Link to="/menu">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Beranda
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20 text-ink-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : current ? (
        <div className="space-y-6">
          <CloseShiftSection onClosed={setClosed} />
          <Button variant="outline" className="w-full gap-2" onClick={() => window.print()}>
            Cetak Laporan Shift
          </Button>
        </div>
      ) : closed ? (
        <div className="space-y-6">
          <ClosedShiftCard shift={closed} />
          <Button variant="outline" className="w-full gap-2" onClick={() => window.print()}>
            Cetak Laporan Shift
          </Button>
        </div>
      ) : (
        // No shift and nothing just closed: back to the start, where a cashier is
        // asked to open one.
        <Navigate to="/" replace />
      )}
    </div>
  );
}

/** What the close recorded: expected, counted and the difference. */
function ClosedShiftCard({ shift }: { shift: Shift }) {
  const variance = shift.variance ?? 0;
  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <p className="flex items-center gap-2 font-display text-base font-semibold text-emerald-800">
          <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> Shift ditutup
          {shift.closedAt ? ` pukul ${formatTime(shift.closedAt)}` : ''}
        </p>
        <dl className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-ink-400">Diharapkan</dt>
            <dd className="tnum font-display text-lg text-ink-900">{rupiah(shift.expectedCash ?? 0)}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-ink-400">Dihitung</dt>
            <dd className="tnum font-display text-lg text-ink-900">{rupiah(shift.countedCash ?? 0)}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-ink-400">Selisih</dt>
            <dd className="tnum font-display text-lg text-ink-900">
              {variance === 0 ? 'Sesuai' : `${variance > 0 ? 'Lebih' : 'Kurang'} ${rupiah(Math.abs(variance))}`}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
